import React, { useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import EstimatItemRow from './EstimatItemRow';
import EstimatSubcategoryEdit from './EstimatSubcategoryEdit';
import EstimatSummaryRows from './EstimatSummaryRows';
import { radrubrikFor, radrubrikKortFor } from '../../utils/kategoriRubriker';

/**
 * Bygg en sorterad lista av alla items (standard + custom) för en flat kategori.
 * Tilldelar sortOrder om det saknas (bakåtkompatibelt med gamla estimat).
 */
const buildSortedFlatItems = (categoryKey, category, formData) => {
  const categoryData = formData.estimat?.[categoryKey] || {};

  // Standard-items från config
  const standardItems = (category.items || []).map((cfgItem, idx) => {
    const data = categoryData[cfgItem.name] || {
      selected: false,
      quantity: cfgItem.defaultQuantity,
      unit: cfgItem.unit,
      price: cfgItem.price
    };
    return {
      key: cfgItem.name,
      item: cfgItem,
      estimatItem: data,
      sortOrder: data.sortOrder ?? idx,
    };
  });

  // Custom-items
  const customKeys = Object.keys(categoryData).filter(k => k.startsWith('custom_'));
  const baseOrder = standardItems.length;
  const customItems = customKeys.map((ck, idx) => {
    const data = categoryData[ck] || { selected: true, quantity: 1, unit: 'st', price: 0, label: 'Ny rad', isCustom: true };
    return {
      key: ck,
      item: { name: ck, label: 'Ny rad', unit: 'st', price: 0, isCustom: true },
      estimatItem: data,
      sortOrder: data.sortOrder ?? (baseOrder + idx),
    };
  });

  return [...standardItems, ...customItems].sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * FlatCategoryDnD — Renderar flat items med drag & drop
 */
const FlatCategoryDnD = ({
  categoryKey, category, formData, updateFormData,
  customerComments, byggkonsultResponses, showInlineComments,
  editingLabels, tempLabelValues, editingPrices, tempPriceValues,
  updateEstimat, handleRemoveItem,
  startEditLabel, changeTempLabel, autoSaveLabel,
  startEditPrice, changeTempPrice, autoSavePrice,
  toggleInlineComment, handleEditByggkonsultEntry, onOpenInternalNoteModal, getInternalNote, hasInternalNote,
  expandedSubItems, toggleSubItemsExpanded, addSubItem, updateSubItem, removeSubItem, toggleUseSubItemsTotal,
  handleReorderItems,
}) => {
  const sortedItems = useMemo(
    () => buildSortedFlatItems(categoryKey, category, formData),
    [categoryKey, category, formData]
  );

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const sortedKeys = sortedItems.map(i => i.key);
    handleReorderItems(categoryKey, null, sortedKeys, result.source.index, result.destination.index);
  }, [sortedItems, categoryKey, handleReorderItems]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={`flat-${categoryKey}`}>
        {(provided) => (
          <tbody
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="bg-white divide-y divide-gray-200"
          >
            {sortedItems.map((entry, index) => (
              <Draggable key={entry.key} draggableId={`${categoryKey}--${entry.key}`} index={index}>
                {(dragProvided, snapshot) => (
                  <EstimatItemRow
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    dragHandleProps={dragProvided.dragHandleProps}
                    isDragging={snapshot.isDragging}
                    item={entry.item}
                    estimatItem={entry.estimatItem}
                    categoryKey={categoryKey}
                    subcategoryKey={null}
                    itemKey={`${categoryKey}_${entry.key}`}
                    customerComments={customerComments}
                    byggkonsultResponses={byggkonsultResponses}
                    showInlineComments={showInlineComments}
                    editingLabels={editingLabels}
                    tempLabelValues={tempLabelValues}
                    editingPrices={editingPrices}
                    tempPriceValues={tempPriceValues}
                    updateEstimat={updateEstimat}
                    handleRemoveItem={handleRemoveItem}
                    startEditLabel={startEditLabel}
                    changeTempLabel={changeTempLabel}
                    autoSaveLabel={autoSaveLabel}
                    startEditPrice={startEditPrice}
                    changeTempPrice={changeTempPrice}
                    autoSavePrice={autoSavePrice}
                    toggleInlineComment={toggleInlineComment}
                    handleEditByggkonsultEntry={handleEditByggkonsultEntry}
                    onOpenInternalNoteModal={onOpenInternalNoteModal}
                    getInternalNote={getInternalNote}
                    hasInternalNote={hasInternalNote}
                    isSubItemsExpanded={expandedSubItems[`${categoryKey}_${entry.key}`] || false}
                    toggleSubItemsExpanded={toggleSubItemsExpanded}
                    addSubItem={addSubItem}
                    updateSubItem={updateSubItem}
                    removeSubItem={removeSubItem}
                    toggleUseSubItemsTotal={toggleUseSubItemsTotal}
                  />
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            <EstimatSummaryRows
              categoryKey={categoryKey}
              formData={formData}
              updateFormData={updateFormData}
            />
          </tbody>
        )}
      </Droppable>
    </DragDropContext>
  );
};

/**
 * EstimatCategoryEdit - Renderar en hel kategori med header, items och summeringar för edit-läge
 * 
 * @param {string} categoryKey - Kategori-nyckel
 * @param {Object} category - Category data från config
 * @param {Object} formData - Form data
 * @param {Object} customerComments - Kundens kommentarer
 * @param {Object} byggkonsultResponses - Byggkonsultens svar
 * @param {Object} showInlineComments - State för vilka kommentarer som visas
 * @param {Object} editingLabels - State för vilka labels som redigeras
 * @param {Object} tempLabelValues - Temporära label-värden
 * @param {Object} editingPrices - State för vilka priser som redigeras
 * @param {Object} tempPriceValues - Temporära pris-värden
 * @param {Object} expandedSections - State för vilka sektioner som är expanderade
 * @param {Object} expandedSubcategories - State för vilka subcategories som är expanderade
 * @param {Function} updateEstimat - Uppdatera estimat
 * @param {Function} updateFormData - Uppdatera form data
 * @param {Function} handleRemoveItem - Ta bort item
 * @param {Function} handleAddCustomItem - Lägg till custom item
 * @param {Function} openAddStandardvalModal - Öppna standardval-modal
 * @param {Function} startEditLabel - Starta label-redigering
 * @param {Function} changeTempLabel - Ändra temporär label
 * @param {Function} autoSaveLabel - Auto-spara label
 * @param {Function} startEditPrice - Starta pris-redigering
 * @param {Function} changeTempPrice - Ändra temporärt pris
 * @param {Function} autoSavePrice - Auto-spara pris
 * @param {Function} toggleInlineComment - Toggla inline-kommentar
 * @param {Function} toggleSection - Toggla sektion expand/collapse
 * @param {Function} toggleSubcategory - Toggla subcategory expand/collapse
 * @param {Function} onOpenInternalNoteModal - Öppna modal för intern förklaring
 * @param {Function} getInternalNote - Hämta global intern förklaring för en item
 * @param {Function} hasInternalNote - Kontrollera om en item har global intern förklaring
 * @param {Object} expandedSubItems - State för vilka items som har sina underrader expanderade
 * @param {Function} toggleSubItemsExpanded - Toggla underrader expand/collapse
 * @param {Function} addSubItem - Lägg till underrad
 * @param {Function} updateSubItem - Uppdatera underrad
 * @param {Function} removeSubItem - Ta bort underrad
 * @param {Function} toggleUseSubItemsTotal - Växla prisläge för underrader
 */
const EstimatCategoryEdit = ({
  categoryKey,
  category,
  formData,
  customerComments,
  byggkonsultResponses,
  showInlineComments,
  editingLabels,
  tempLabelValues,
  editingPrices,
  tempPriceValues,
  expandedSections,
  expandedSubcategories,
  updateEstimat,
  updateFormData,
  handleRemoveItem,
  handleAddCustomItem,
  openAddStandardvalModal,
  startEditLabel,
  changeTempLabel,
  autoSaveLabel,
  startEditPrice,
  changeTempPrice,
  autoSavePrice,
  toggleInlineComment,
  handleEditByggkonsultEntry,
  toggleSection,
  toggleSubcategory,
  onOpenInternalNoteModal,
  getInternalNote,
  hasInternalNote,
  // SubItems props
  expandedSubItems,
  toggleSubItemsExpanded,
  addSubItem,
  updateSubItem,
  removeSubItem,
  toggleUseSubItemsTotal,
  // Drag & drop
  handleReorderItems,
}) => {
  // Om ingen explicit state finns, fallbacka till defaultExpanded från config
  const isExpanded = expandedSections[categoryKey] !== undefined 
    ? expandedSections[categoryKey] 
    : (category.defaultExpanded || false);

  return (
    <div key={categoryKey} className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Category header */}
      <div 
        className={`${category.color} px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-between`}
        onClick={() => toggleSection(categoryKey)}
      >
        <h3 className="font-semibold text-white">{category.name}</h3>
        <div className="text-white transition-transform duration-200">
          {isExpanded ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div className="overflow-x-visible">
          {/* Lägg till-knappar för flat categories (inte kategorier med subcategories) */}
          {categoryKey !== 'byggoffert' && categoryKey !== 'inredningsarkitekt' && (
            <div className="flex justify-start gap-2 px-3 sm:px-4 pt-3 sm:pt-4 pb-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleAddCustomItem(categoryKey); }}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded bg-leadme-pink hover:bg-leadme-pink-dark text-white shadow-sm"
                aria-label={`Lägg till rad i ${category.name}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Lägg till
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); openAddStandardvalModal(categoryKey); }}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded bg-gray-600 hover:bg-gray-700 text-white shadow-sm"
                aria-label={`Lägg till standardval i ${category.name}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span className="hidden sm:inline">Lägg till standardval</span>
                <span className="sm:hidden">Standardval</span>
              </button>
            </div>
          )}
          
          <div className="overflow-x-auto -mx-px">
          <table className="w-full table-fixed min-w-[580px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-1 sm:px-2 py-1.5 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase w-[5%] whitespace-nowrap"><span className="hidden sm:inline">Ändra</span></th>
                <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase w-[28%] whitespace-nowrap"><span className="sm:hidden">{radrubrikKortFor(categoryKey)}</span><span className="hidden sm:inline">{radrubrikFor(categoryKey)}</span></th>
                <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase w-[6%] whitespace-nowrap">Välj</th>
                <th className="px-3 sm:px-6 py-1.5 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase w-[15%] whitespace-nowrap"><span className="sm:hidden">A-pris</span><span className="hidden sm:inline">A-pris (inkl. moms)</span></th>
                <th className="px-1 sm:px-2 py-1.5 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase w-[10%] whitespace-nowrap">Antal</th>
                <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase w-[8%] whitespace-nowrap">Enhet</th>
                <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase w-[14%] whitespace-nowrap">Summa</th>
                <th className="p-0 sm:px-4 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase w-0 max-w-0 sm:max-w-none sm:w-[16%] overflow-hidden whitespace-nowrap"><span className="hidden sm:inline">Kommentarer</span></th>
              </tr>
            </thead>
            {category.subcategories ? (
              <>
                {/* Hantera subcategories — varje subcategory renderar sin egen tbody */}
                {Object.entries(category.subcategories).map(([subcategoryKey, subcategory]) => (
                  <EstimatSubcategoryEdit
                    key={subcategoryKey}
                    categoryKey={categoryKey}
                    subcategoryKey={subcategoryKey}
                    subcategory={subcategory}
                    formData={formData}
                    customerComments={customerComments}
                    byggkonsultResponses={byggkonsultResponses}
                    showInlineComments={showInlineComments}
                    editingLabels={editingLabels}
                    tempLabelValues={tempLabelValues}
                    editingPrices={editingPrices}
                    tempPriceValues={tempPriceValues}
                    updateEstimat={updateEstimat}
                    updateFormData={updateFormData}
                    handleRemoveItem={handleRemoveItem}
                    handleAddCustomItem={handleAddCustomItem}
                    openAddStandardvalModal={openAddStandardvalModal}
                    startEditLabel={startEditLabel}
                    changeTempLabel={changeTempLabel}
                    autoSaveLabel={autoSaveLabel}
                    startEditPrice={startEditPrice}
                    changeTempPrice={changeTempPrice}
                    autoSavePrice={autoSavePrice}
                    toggleInlineComment={toggleInlineComment}
                    handleEditByggkonsultEntry={handleEditByggkonsultEntry}
                    expandedSubcategories={expandedSubcategories}
                    toggleSubcategory={toggleSubcategory}
                    onOpenInternalNoteModal={onOpenInternalNoteModal}
                    getInternalNote={getInternalNote}
                    hasInternalNote={hasInternalNote}
                    expandedSubItems={expandedSubItems}
                    toggleSubItemsExpanded={toggleSubItemsExpanded}
                    addSubItem={addSubItem}
                    updateSubItem={updateSubItem}
                    removeSubItem={removeSubItem}
                    toggleUseSubItemsTotal={toggleUseSubItemsTotal}
                    handleReorderItems={handleReorderItems}
                  />
                ))}

                {/* Summeringsrader för subcategory-baserade kategorier */}
                <tbody className="bg-white">
                  <EstimatSummaryRows
                    categoryKey={categoryKey}
                    formData={formData}
                    updateFormData={updateFormData}
                  />
                </tbody>
              </>
            ) : (
              <FlatCategoryDnD
                categoryKey={categoryKey}
                category={category}
                formData={formData}
                updateFormData={updateFormData}
                customerComments={customerComments}
                byggkonsultResponses={byggkonsultResponses}
                showInlineComments={showInlineComments}
                editingLabels={editingLabels}
                tempLabelValues={tempLabelValues}
                editingPrices={editingPrices}
                tempPriceValues={tempPriceValues}
                updateEstimat={updateEstimat}
                handleRemoveItem={handleRemoveItem}
                startEditLabel={startEditLabel}
                changeTempLabel={changeTempLabel}
                autoSaveLabel={autoSaveLabel}
                startEditPrice={startEditPrice}
                changeTempPrice={changeTempPrice}
                autoSavePrice={autoSavePrice}
                toggleInlineComment={toggleInlineComment}
                handleEditByggkonsultEntry={handleEditByggkonsultEntry}
                onOpenInternalNoteModal={onOpenInternalNoteModal}
                getInternalNote={getInternalNote}
                hasInternalNote={hasInternalNote}
                expandedSubItems={expandedSubItems}
                toggleSubItemsExpanded={toggleSubItemsExpanded}
                addSubItem={addSubItem}
                updateSubItem={updateSubItem}
                removeSubItem={removeSubItem}
                toggleUseSubItemsTotal={toggleUseSubItemsTotal}
                handleReorderItems={handleReorderItems}
              />
            )}
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimatCategoryEdit;

